/* Copyright (c) 2026 PearlCrm Pty Ltd. All rights reserved. */

import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Alert, Card, CircularProgress, TextField } from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import { getApiBaseUrl } from "utils/apiBaseUrl";

const formatApiError = (err) => {
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail.map((item) => item?.msg || JSON.stringify(item)).join("; ");
  }
  if (detail && typeof detail === "object") {
    return detail.message || JSON.stringify(detail);
  }
  const body = err?.response?.data;
  if (typeof body?.message === "string" && body.message) return body.message;
  if (typeof body?.error === "string" && body.error) return body.error;
  return err?.message || "Something went wrong. Please try again.";
};

export default function MerchantOrderLineRespond() {
  const { token } = useParams();
  const apiBaseUrl = useMemo(() => getApiBaseUrl(), []);
  const client = useMemo(
    () =>
      axios.create({
        baseURL: apiBaseUrl,
        timeout: 60000,
      }),
    [apiBaseUrl]
  );

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [confirmationNo, setConfirmationNo] = useState("");

  useEffect(() => {
    if (!token) return undefined;
    let cancelled = false;
    setLoading(true);
    setError("");
    client
      .get(`/merchant-order-lines/public/respond/${encodeURIComponent(token)}`)
      .then((res) => {
        if (!cancelled) {
          setPreview(res.data);
          setConfirmationNo(res.data?.merchant_confirmation_no || "");
        }
      })
      .catch((err) => {
        if (!cancelled) setError(formatApiError(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [client, token]);

  const handleRespond = async () => {
    if (!token || submitting) return;
    setSubmitting(true);
    setError("");
    try {
      const res = await client.post(
        `/merchant-order-lines/public/respond/${encodeURIComponent(token)}`,
        { merchant_confirmation_no: confirmationNo || null }
      );
      setResult(res.data);
      if (res.data?.status) {
        setPreview((prev) => (prev ? { ...prev, status: res.data.status } : prev));
      }
    } catch (err) {
      // Accept may have already committed server-side (Booked) while a follow-up
      // side effect timed out. Re-check preview so we don't leave the merchant
      // staring at a permanent "Submitting…" / false failure.
      try {
        const previewRes = await client.get(
          `/merchant-order-lines/public/respond/${encodeURIComponent(token)}`
        );
        const latest = previewRes.data;
        setPreview(latest);
        if (latest?.status && latest.status !== "Waiting For Merchant Acknowledgment") {
          // Prefer the token action over status — a decline must never show as accepted.
          const wasReject =
            preview?.action === "reject" ||
            String(latest.status).toLowerCase().includes("reject") ||
            String(latest.status).toLowerCase().includes("declin") ||
            String(latest.status).toLowerCase().includes("cancel");
          setResult({
            success: true,
            status: latest.status,
            message: wasReject
              ? `Service request has been declined (${latest.status})`
              : latest.status === "Booked"
                ? "Service request has been accepted"
                : `Service request is already ${latest.status}`,
          });
          return;
        }
      } catch {
        // Keep the original error below.
      }
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const isReject = preview?.action === "reject";
  const actionLabel = isReject ? "Reject Service Request" : "Accept Service Request";
  const alreadyDone = preview?.status && preview.status !== "Waiting For Merchant Acknowledgment";

  return (
    <MDBox
      minHeight="100vh"
      display="flex"
      alignItems="center"
      justifyContent="center"
      px={2}
      py={4}
      sx={{ background: "linear-gradient(180deg, #E8F1FB 0%, #F7F9FC 55%, #FFFFFF 100%)" }}
    >
      <Card sx={{ width: "100%", maxWidth: 560, p: 3 }}>
        <MDTypography variant="h5" fontWeight="bold" mb={1}>
          Merchant Service Request
        </MDTypography>
        <MDTypography variant="body2" color="text" mb={2}>
          Confirm this booked service
        </MDTypography>

        {loading ? (
          <MDBox display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </MDBox>
        ) : error && !preview ? (
          <Alert severity="error">{error}</Alert>
        ) : (
          <>
            {preview && (
              <MDBox mb={2}>
                <MDTypography variant="body2"><strong>Line:</strong> {preview.mol_id}</MDTypography>
                <MDTypography variant="body2"><strong>Merchant:</strong> {preview.merchant_name}</MDTypography>
                <MDTypography variant="body2"><strong>Booking:</strong> {preview.order_name}</MDTypography>
                <MDTypography variant="body2"><strong>Service:</strong> {preview.service_title}</MDTypography>
                <MDTypography variant="body2"><strong>Trip:</strong> {preview.package_title}</MDTypography>
                <MDTypography variant="body2"><strong>Pax:</strong> {preview.pax_count}</MDTypography>
                <MDTypography variant="body2"><strong>Dates:</strong> {preview.start_date} – {preview.end_date}</MDTypography>
                <MDTypography variant="body2"><strong>Current status:</strong> {preview.status}</MDTypography>
              </MDBox>
            )}

            {result ? (
              <Alert severity={result.success === false ? "warning" : "success"}>
                {result.message}
              </Alert>
            ) : alreadyDone ? (
              <Alert severity="info">
                This merchant order line is already {preview.status}.
              </Alert>
            ) : (
              <>
                {!isReject && (
                  <TextField
                    fullWidth
                    label="Merchant Confirmation No (optional)"
                    value={confirmationNo}
                    onChange={(e) => setConfirmationNo(e.target.value)}
                    sx={{ mb: 2 }}
                  />
                )}
                <MDButton
                  variant="gradient"
                  color={isReject ? "error" : "success"}
                  onClick={handleRespond}
                  disabled={submitting}
                  fullWidth
                >
                  {submitting ? "Submitting…" : actionLabel}
                </MDButton>
              </>
            )}

            {error && preview ? (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            ) : null}

            {preview?.company_name ? (
              <MDTypography
                variant="caption"
                color="text"
                display="block"
                textAlign="center"
                mt={2.5}
                sx={{ opacity: 0.75 }}
              >
                Powered by {preview.company_name}
              </MDTypography>
            ) : null}
          </>
        )}
      </Card>
    </MDBox>
  );
}
